import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Check, FolderTree, FolderOpen, Pencil, Send, Trash2, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { BrowseResourcesDialog } from "@/components/constructor/browse-resources";
import { ScreenshotLightbox, type ScreenshotRef } from "@/components/constructor/screenshot-lightbox";
import {
    createDraft,
    discardDraft,
    getDraft,
    publishDraft,
    renameDraft,
    uploadDraft,
} from "@/api/constructor";
import { getAdvertiserDashboard, listAdvertisers } from "@/api/commerce";
import { ReassignAdvertiserPopover } from "@/components/reassign-advertiser-popover";
import { ConfirmDialog, useConfirm } from "@/components/ui/confirm-dialog";
import { PreviewFrame } from "@/components/constructor/preview-frame";
import { FileTree } from "@/components/constructor/file-tree";
import { FileView, type ViewSelection } from "@/components/constructor/file-view";
import { BannersPanel } from "@/components/constructor/banners-panel";
import { ChatPanel } from "@/components/constructor/chat-panel";
import type { DraftDto } from "@/types/constructor";
import type { CrossAdvertiserPublishError } from "@/types/commerce";
import { apiErrorMessage } from "@/lib/api";
import { AxiosError } from "axios";

// Default to the same-origin local preview path (Spring serves it via
// AdminPreviewController). Set VITE_PREVIEW_WORKER_BASE to opt into the
// real Cloudflare Worker preview (separate subdomain — better isolation).
const PREVIEW_OVERRIDE = (import.meta.env.VITE_PREVIEW_WORKER_BASE as string | undefined) || "";

function buildPreviewUrl(draftId: number, cacheBust: number): string {
    const qs = `?_=${cacheBust}`;
    if (PREVIEW_OVERRIDE) return `${PREVIEW_OVERRIDE}/preview/${draftId}/${qs}`;
    return `/admin/preview/draft/${draftId}/${qs}`;
}

/**
 * Single-pane constructor: split-view of preview iframe + chat + collapsible
 * file tree. Three modes by URL:
 *   - /pages/new?mode=ai          → blank draft
 *   - /pages/new?mode=ai-upload   → drop a zip first
 *   - /pages/{pageId}/edit         → fork latest version of a page
 *
 * Phase 1 ships the structural shell + file editing. Chat + the agent loop
 * land in Phase 4.
 */
export function ConstructorPage(): React.ReactNode {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const params = useParams<{ id?: string; draftId?: string }>();
    const [search] = useSearchParams();
    const mode = search.get("mode");

    const [draftId, setDraftId] = React.useState<number | null>(
        params.draftId ? Number(params.draftId) : null,
    );
    const [bootstrapping, setBootstrapping] = React.useState(true);
    const [bootstrapTimedOut, setBootstrapTimedOut] = React.useState(false);
    // Where to go back to:
    //   - /pages/:id/edit  → page detail (we're editing an existing page)
    //   - /constructor/draft/:draftId → /constructor (drafts list — operator
    //     opened this from there)
    //   - /pages/new?mode=ai* → /constructor (these create a fresh draft and
    //     belong to the constructor surface, not the pages list)
    const backTarget = params.id
        ? `/pages/${params.id}`
        : "/constructor";
    const [showFileTree, setShowFileTree] = React.useState(true);
    const [selected, setSelected] = React.useState<ViewSelection | null>(null);
    const [centerTab, setCenterTab] = React.useState<"preview" | "banners">("preview");
    const [browseOpen, setBrowseOpen] = React.useState(false);
    const [shotInLightbox, setShotInLightbox] = React.useState<ScreenshotRef | null>(null);
    const [previewBust, setPreviewBust] = React.useState(0);
    const [autoPrompt, setAutoPrompt] = React.useState<string | null>(null);

    // Bootstrap: figure out which draft to open based on URL.
    React.useEffect(() => {
        let cancelled = false;
        // 30s watchdog: if bootstrap hangs (createDraft slow / network), show
        // the operator a recoverable error instead of an eternal spinner.
        const timeoutId = window.setTimeout(() => {
            if (!cancelled) setBootstrapTimedOut(true);
        }, 30_000);
        (async () => {
            try {
                if (params.draftId) {
                    setDraftId(Number(params.draftId));
                    return;
                }
                if (params.id) {
                    // /pages/{id}/edit — fork the latest version into a fresh draft.
                    const draft = await createDraft({ mode: "fork", pageId: Number(params.id) });
                    if (!cancelled) setDraftId(draft.id);
                    return;
                }
                // /pages/new branch.
                if (mode === "ai-upload") {
                    // Wait for the user to drop a file; don't auto-create a draft.
                    return;
                }
                const draft = await createDraft({ mode: "blank" });
                if (!cancelled) setDraftId(draft.id);
            } catch (err) {
                toast.error(apiErrorMessage(err, "Couldn't open the constructor"));
                if (!cancelled) setBootstrapTimedOut(true);
            } finally {
                window.clearTimeout(timeoutId);
                if (!cancelled) setBootstrapping(false);
            }
        })();
        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [params.id, params.draftId, mode]);

    const draftQuery = useQuery({
        queryKey: ["draft", draftId],
        queryFn: () => getDraft(draftId!),
        enabled: !!draftId,
    });

    // Bump the preview cache-buster whenever any file/asset query invalidates.
    React.useEffect(() => {
        const unsub = queryClient.getQueryCache().subscribe((event) => {
            if (event?.type === "updated" && event.query.queryKey[0] === "draft" && draftId) {
                setPreviewBust((x) => x + 1);
            }
        });
        return () => unsub();
    }, [queryClient, draftId]);

    const uploadMut = useMutation({
        mutationFn: (file: File) => uploadDraft(file),
        onSuccess: (draft) => {
            setDraftId(draft.id);
            // Auto-prompt the AI to audit the freshly imported bundle.
            setAutoPrompt(
                "I just imported this bundle. Please audit it: run validate_bundle and render_page on it, "
                + "then summarise any issues (missing manifest, broken form contracts, console errors, broken assets, "
                + "non-hijacked forms). If you spot easy wins, propose specific fixes I can approve."
            );
            toast.success(`Imported ${draft.name}`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Upload failed")),
    });

    // Local state for the cross-advertiser confirm dialog (HTTP 409 from publish).
    const [ownershipConflict, setOwnershipConflict] =
        React.useState<CrossAdvertiserPublishError | null>(null);
    // Cached advertiser list — used to translate the conflict's numeric ids
    // into human names when the dialog renders.
    const advertisersQuery = useQuery({
        queryKey: ["advertisers", { includeArchived: true }],
        queryFn: () => listAdvertisers({ includeArchived: true }),
        staleTime: 30_000,
    });
    const nameOfAdvertiser = React.useCallback(
        (id: number | null): string => {
            if (id == null) return "Unassigned";
            const a = advertisersQuery.data?.find((x) => x.id === id);
            return a ? a.name : `#${id}`;
        },
        [advertisersQuery.data],
    );

    const publishMut = useMutation({
        mutationFn: ({ confirmOwnershipChange }: { confirmOwnershipChange?: boolean } = {}) =>
            publishDraft(draftId!, { confirmOwnershipChange }),
        onSuccess: (v) => {
            // Show both "go to page" and "keep editing" rather than yanking
            // the operator out of the constructor — common pattern is to
            // publish then iterate again on a fresh draft fork.
            toast.success(`Published v${v.versionNum}`, {
                duration: 8000,
                action: { label: "Go to page", onClick: () => navigate(`/pages/${v.pageId}`) },
                description: "Or keep editing — chat history stays.",
            });
            setOwnershipConflict(null);
            queryClient.invalidateQueries({ queryKey: ["draft", draftId] });
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
        },
        onError: (err) => {
            // 409 → page advertiser != draft advertiser. Surface the structured
            // body in a confirm dialog so the operator can opt in to overwriting
            // the page's owner. Anything else is a generic publish failure.
            if (err instanceof AxiosError && err.response?.status === 409) {
                const body = err.response.data as CrossAdvertiserPublishError | undefined;
                if (body && "currentPageAdvertiserId" in body && "draftAdvertiserId" in body) {
                    setOwnershipConflict(body);
                    return;
                }
            }
            toast.error(apiErrorMessage(err, "Publish failed"));
        },
    });

    // Snapshot-spend warning for the "Already-spent $X stays with {old}" toast
    // on advertiser reassignment from the header. Dashboard exposes the totals
    // we need; null when the draft is unassigned.
    const draftAdvertiserId = draftQuery.data?.advertiserId ?? null;
    const advertiserDashboardQuery = useQuery({
        queryKey: ["advertiser-dashboard", draftAdvertiserId],
        queryFn: () => getAdvertiserDashboard(draftAdvertiserId as number),
        enabled: draftAdvertiserId != null,
        staleTime: 30_000,
    });
    const formatSpend = (micros: number | undefined | null): string => {
        if (!micros || micros <= 0) return "$0";
        const usd = micros / 1_000_000;
        return usd >= 100
            ? `$${usd.toFixed(0)}`
            : `$${usd.toFixed(2)}`;
    };

    const discardMut = useMutation({
        mutationFn: () => discardDraft(draftId!),
        onSuccess: () => {
            toast.success("Draft discarded");
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            navigate(backTarget);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Discard failed")),
    });

    const renameMut = useMutation({
        mutationFn: (newName: string) => renameDraft(draftId!, newName),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["draft", draftId] });
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            setNameEditing(false);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Rename failed")),
    });
    const [nameEditing, setNameEditing] = React.useState(false);
    const [pendingName, setPendingName] = React.useState("");

    if (bootstrapping && !bootstrapTimedOut) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-muted-foreground">Opening the constructor…</p>
                <button
                    type="button"
                    onClick={() => navigate(backTarget)}
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                    Cancel and go back
                </button>
            </div>
        );
    }
    if (bootstrapTimedOut && !draftId) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-3 p-8 text-center">
                <h1 className="text-lg font-semibold">Couldn't open the constructor</h1>
                <p className="max-w-sm text-sm text-muted-foreground">
                    The draft creation request didn't return in 30 seconds. The backend may be slow or
                    unreachable. Try again, or go back.
                </p>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate(backTarget)}>
                        <ArrowLeft className="h-4 w-4" /> Back
                    </Button>
                    <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
            </div>
        );
    }

    // Upload mode + no draft yet → drop zone.
    if (mode === "ai-upload" && !draftId) {
        return <UploadGate onUpload={(f) => uploadMut.mutate(f)} pending={uploadMut.isPending} />;
    }

    if (!draftId) {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
                <h1 className="text-xl font-semibold">No draft to edit</h1>
                <p className="text-sm text-muted-foreground">
                    Open <code>/pages/new?mode=ai</code> to start a fresh draft.
                </p>
                <Button onClick={() => navigate("/pages")}>Back to pages</Button>
            </div>
        );
    }

    const draft: DraftDto | undefined = draftQuery.data;

    return (
        <div className="flex h-screen flex-col bg-background">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(backTarget)}
                        title={params.id ? "Back to page" : "Back to pages"}
                        aria-label="Back"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowFileTree((x) => !x)}
                        title={showFileTree ? "Hide file tree" : "Show file tree"}
                        aria-label="Toggle file tree"
                    >
                        <FolderTree className="h-4 w-4" />
                    </Button>
                    {nameEditing && draft ? (
                        <form
                            className="flex items-center gap-1"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const v = pendingName.trim();
                                if (!v || v === draft.name) { setNameEditing(false); return; }
                                renameMut.mutate(v);
                            }}
                        >
                            <Input
                                autoFocus
                                value={pendingName}
                                maxLength={160}
                                onChange={(e) => setPendingName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") setNameEditing(false);
                                }}
                                className="h-7 w-64 text-sm"
                                data-testid="constructor-rename-input"
                            />
                            <Button type="submit" variant="ghost" size="sm" disabled={renameMut.isPending} data-testid="constructor-rename-save">
                                <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => setNameEditing(false)}>
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </form>
                    ) : (
                        <button
                            type="button"
                            className="group inline-flex items-center gap-1 rounded px-1 text-sm font-medium hover:bg-muted"
                            onClick={() => {
                                if (!draft) return;
                                setPendingName(draft.name);
                                setNameEditing(true);
                            }}
                            disabled={!draft}
                            title={draft ? "Rename draft" : ""}
                            data-testid="constructor-rename"
                        >
                            <span>{draft?.name ?? "Loading…"}</span>
                            {draft && <Pencil className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
                        </button>
                    )}
                    {draft && (
                        <ReassignAdvertiserPopover
                            entityType="page_draft"
                            entityId={draft.id}
                            currentAdvertiserId={draft.advertiserId ?? null}
                            data-testid="constructor-advertiser-dialog"
                            successToast={({ oldName, newName }) => {
                                const spent = formatSpend(
                                    advertiserDashboardQuery.data?.totalSpendUsdMicros,
                                );
                                return {
                                    variant: "warning",
                                    title: `Future tool spend will attribute to ${newName}.`,
                                    description: `Already-spent ${spent} stays with ${oldName}.`,
                                };
                            }}
                            trigger={
                                <button
                                    type="button"
                                    title="Reassign advertiser"
                                    aria-label="Reassign advertiser"
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted"
                                    data-testid="constructor-advertiser-badge"
                                >
                                    <span className="text-muted-foreground">Advertiser:</span>
                                    <span
                                        className={
                                            draft.advertiserId == null
                                                ? "italic text-muted-foreground"
                                                : "font-medium"
                                        }
                                    >
                                        {nameOfAdvertiser(draft.advertiserId ?? null)}
                                    </span>
                                    <Pencil
                                        className="h-3 w-3 text-muted-foreground"
                                        aria-hidden="true"
                                    />
                                </button>
                            }
                        />
                    )}
                    {draft && (
                        <span className="text-xs text-muted-foreground">
                            {draft.fileCount} file{draft.fileCount === 1 ? "" : "s"} · {draft.assetCount} asset
                            {draft.assetCount === 1 ? "" : "s"}
                            <span className="ml-2 italic">· auto-saved</span>
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBrowseOpen(true)}
                        title="Browse files, assets, and screenshots"
                        data-testid="browse-resources-button"
                    >
                        <FolderOpen className="h-3.5 w-3.5" />
                        Browse
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => confirm.show({
                            title: `Discard "${draft?.name ?? "this draft"}"?`,
                            description: "Removes the draft and its chat history. Any published versions are unaffected.",
                            destructive: true,
                            confirmLabel: "Discard draft",
                            "data-testid": "discard-draft-confirm",
                            onConfirm: async () => {
                                await new Promise<void>((resolve, reject) => {
                                    discardMut.mutate(undefined, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                });
                            },
                        })}
                        disabled={!draft || discardMut.isPending}
                        data-testid="discard-draft"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Discard
                    </Button>
                    <Button
                        variant="default"
                        size="sm"
                        disabled={publishMut.isPending || !draft || draft.fileCount === 0}
                        title={!draft || draft.fileCount === 0
                            ? "Draft has no files — add at least one before publishing."
                            : "Mint a new immutable page version. Live domains pointing at older versions update automatically."}
                        onClick={() => publishMut.mutate({})}
                        data-testid="publish-draft"
                    >
                        <Send className="h-3.5 w-3.5" />
                        Publish
                    </Button>
                </div>
            </div>

            {/* Body — file tree | preview-or-editor | chat */}
            <div className="flex flex-1 overflow-hidden">
                {showFileTree && (
                    <div className="flex w-60 shrink-0 flex-col">
                        <FileTree draftId={draftId} selected={selected} onSelect={setSelected} />
                    </div>
                )}
                <div className="flex flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-center gap-1 border-b border-border bg-background px-2 py-1">
                        <button
                            type="button"
                            onClick={() => setCenterTab("preview")}
                            className={`rounded px-3 py-1 text-xs ${centerTab === "preview" ? "bg-muted font-medium" : "text-muted-foreground hover:bg-muted/60"}`}
                            data-testid="center-tab-preview"
                        >
                            Preview
                        </button>
                        <button
                            type="button"
                            onClick={() => setCenterTab("banners")}
                            className={`rounded px-3 py-1 text-xs ${centerTab === "banners" ? "bg-muted font-medium" : "text-muted-foreground hover:bg-muted/60"}`}
                            data-testid="center-tab-banners"
                        >
                            Banners
                        </button>
                    </div>
                    {centerTab === "preview" ? (
                        selected ? (
                            <div className="flex flex-1 overflow-hidden">
                                <div className="flex w-1/2 flex-col border-r border-border">
                                    <FileView
                                        draftId={draftId}
                                        selection={selected}
                                        onClose={() => setSelected(null)}
                                    />
                                </div>
                                <div className="w-1/2">
                                    <PreviewFrame previewUrl={buildPreviewUrl(draftId, previewBust)} />
                                </div>
                            </div>
                        ) : (
                            <PreviewFrame previewUrl={buildPreviewUrl(draftId, previewBust)} />
                        )
                    ) : (
                        <BannersPanel pageId={draftQuery.data?.pageId ?? null} />
                    )}
                </div>
                <div className="flex w-[420px] shrink-0">
                    <ChatPanel
                        draftId={draftId}
                        onPreviewChanged={() => setPreviewBust((x) => x + 1)}
                        autoPrompt={autoPrompt}
                        onAutoPromptSent={() => setAutoPrompt(null)}
                        onOpenFile={(path) => setSelected({ kind: "file", path })}
                        onOpenScreenshot={(s) => setShotInLightbox({ id: s.id, url: s.url, label: s.label })}
                    />
                </div>
            </div>

            <BrowseResourcesDialog
                draftId={draftId}
                open={browseOpen}
                onClose={() => setBrowseOpen(false)}
                onOpenFile={(path) => setSelected({ kind: "file", path })}
                onOpenScreenshot={(s) => setShotInLightbox({
                    id: s.id, url: s.url, label: s.label, capturedAt: s.capturedAt,
                })}
            />
            <ScreenshotLightbox shot={shotInLightbox} onClose={() => setShotInLightbox(null)} />
            {/*
             * Cross-advertiser publish confirmation. Triggered by HTTP 409 from
             * POST /admin/constructor/drafts/{id}/publish. On confirm we re-issue
             * the publish with ?confirmOwnershipChange=true; the backend then
             * overwrites the page's advertiser_id and logs the change.
             */}
            <ConfirmDialog
                open={ownershipConflict != null}
                onOpenChange={(o) => { if (!o) setOwnershipConflict(null); }}
                title="Page and draft have different advertisers"
                description={
                    ownershipConflict ? (
                        <>
                            Page is owned by{" "}
                            <span className="font-medium text-foreground">
                                {nameOfAdvertiser(ownershipConflict.currentPageAdvertiserId)}
                            </span>
                            , draft by{" "}
                            <span className="font-medium text-foreground">
                                {nameOfAdvertiser(ownershipConflict.draftAdvertiserId)}
                            </span>
                            . Publishing will promote draft ownership. Continue?
                        </>
                    ) : null
                }
                confirmLabel="Publish & overwrite owner"
                cancelLabel="Cancel"
                loading={publishMut.isPending}
                onConfirm={() => publishMut.mutate({ confirmOwnershipChange: true })}
                data-testid="cross-advertiser-publish-confirm"
            />
            {confirm.dialog}
        </div>
    );
}

function UploadGate({
    onUpload,
    pending,
}: {
    onUpload: (file: File) => void;
    pending: boolean;
}): React.ReactNode {
    const [dragOver, setDragOver] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onUpload(file);
    };

    return (
        <div className="flex h-screen flex-col items-center justify-center gap-6 p-8">
            <h1 className="text-2xl font-semibold">Upload a bundle to adapt</h1>
            <p className="max-w-md text-center text-sm text-muted-foreground">
                Drop a <code>.zip</code> with at least an <code>index.html</code>. The AI will audit it
                and offer to fix tracking, form contracts, and accessibility issues.
            </p>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`flex h-64 w-96 flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-colors ${
                    dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/30"
                }`}
            >
                <Upload className="h-10 w-10 text-muted-foreground" />
                <div className="text-sm font-medium">Drop your bundle here</div>
                <div className="text-xs text-muted-foreground">or click to choose</div>
                {pending && <Spinner className="h-5 w-5" />}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                className="sr-only"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUpload(file);
                }}
            />
            <a href="/pages" className="text-xs text-muted-foreground hover:text-foreground">
                Cancel
            </a>
        </div>
    );
}
